import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CircleAlert, X } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiSelect,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectTrigger,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import { IntuiRadioGroup, IntuiRadioGroupItem } from '@/components/intueri/IntuiRadioGroup'
import { apiService, type DigitalCoworkerVoice } from '@/service/api'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import WizardLayout from './WizardLayout'
import AvatarPicker from './AvatarPicker'

const DEPARTMENTS = [
  'Chemical Engineering',
  'Mechanical Engineering',
  'Electrical Engineering',
  'Default',
] as const

const VOICES: readonly DigitalCoworkerVoice[] = ['female', 'male']

export default function NewDigitalCoworkerV2() {
  const navigate = useNavigate()
  const user = useAppStore((s) => s.user)
  const location = useLocation()
  const { t } = useTranslation()
  const [type, setType] = useState<'me' | 'colleague'>('me')
  const [name, setName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [department, setDepartment] = useState('Chemical Engineering')
  const [expertiseTags, setExpertiseTags] = useState<string[]>([])
  // Picked by the person setting the coworker up; never guessed from the name or photo.
  const [voice, setVoice] = useState<DigitalCoworkerVoice | ''>('')
  const [expertiseInput, setExpertiseInput] = useState('')
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  // UI-05: seeded from the coworker being edited, so the edit page shows the
  // avatar the list page already shows instead of falling back to the initial.
  // A lazy initializer rather than an effect: it runs once, so it can never
  // clobber a photo the user picks afterwards.
  //
  // `photoFile` is deliberately NOT seeded. Submit sends
  // `updateDigitalCoworker(id, payload, photoFile ?? undefined)`, so leaving it
  // null is what tells the server to keep the photo it already has — seeding it
  // would re-upload the same avatar on every save.
  const [photoPreview, setPhotoPreview] = useState<string | null>(
    () => (location.state?.avatarUrl as string | null | undefined) ?? null,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [colleagueEmail, setColleagueEmail] = useState('')
  const [isSendingInvite, setIsSendingInvite] = useState(false)
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false)
  const [showColleagueAlert, setShowColleagueAlert] = useState(true)
  const [colleagueExpertiseTags, setColleagueExpertiseTags] = useState<string[]>([])
  const [colleagueExpertiseInput, setColleagueExpertiseInput] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const existingCoworkerId = location.state?.coworkerId as string | undefined
  useEffect(() => {
    const savedData = location.state?.formData
    if (savedData) {
      setType(savedData.type || 'me')
      setName(savedData.name || '')
      setJobTitle(savedData.jobTitle || '')
      const dept = savedData.department
      setDepartment(DEPARTMENTS.includes(dept) ? dept : 'Chemical Engineering')
      const exp = savedData.expertise || ''
      setExpertiseTags(exp ? exp.split(',').map((s: string) => s.trim()).filter(Boolean) : [])
      setVoice(VOICES.includes(savedData.voice) ? savedData.voice : '')
    }
  }, [location.state])

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    const reader = new FileReader()
    reader.onloadend = () => setPhotoPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleSelectPresetAvatar = async (url: string) => {
    setPhotoPreview(url)
    try {
      // Convert SVG/image to PNG blob for backend compatibility
      const img = new Image()
      img.crossOrigin = 'anonymous'
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = reject
        img.src = url
      })
      const canvas = document.createElement('canvas')
      canvas.width = 256
      canvas.height = 256
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, 256, 256)
      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), 'image/png')
      )
      setPhotoFile(new File([blob], 'avatar.png', { type: 'image/png' }))
    } catch {
      setPhotoFile(null)
    }
  }

  const handleSubmit = async () => {
    // Annotators are read-only — creating/editing a digital coworker is blocked.
    if (isAnnotatorRole(user?.role)) return notifyAnnotatorBlocked()
    if (!name.trim() || !jobTitle.trim() || !voice) return
    setIsSubmitting(true)
    try {
      const expertise = expertiseTags.join(', ')
      const formData = { type, name, jobTitle, department, expertise, voice }
      const payload = { name: name.trim(), job_title: jobTitle.trim(), department, expertise: expertise || undefined, type, voice }
      if (existingCoworkerId) {
        await apiService.updateDigitalCoworker(existingCoworkerId, payload, photoFile ?? undefined)
        toast.success(t('digital_coworker:edit_success'))
        navigate('/digital-coworker')
      } else {
        const result = await apiService.createDigitalCoworker(payload, photoFile ?? undefined)
        navigate('/digital-coworker/connect-accounts', {
          state: { formData, coworkerId: result.digital_coworker.id },
        })
      }
    } catch (error) {
      console.error('Failed to save digital coworker:', error)
      toast.error(t(existingCoworkerId ? 'digital_coworker:toast.edit_error' : 'digital_coworker:toast.create_error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSendInvite = async () => {
    if (!colleagueEmail.trim()) return
    setIsSendingInvite(true)
    try {
      const { success } = await apiService.inviteColleague({ colleague_email: colleagueEmail.trim(), webapp_origin: window.location.origin })
      if (!success) throw new Error('invite_failed')
      toast.success(t('digital_coworker:new_coworker.colleague_invite.invite_success'))
      navigate('/digital-coworker/success', {
        state: { coworkerName: name.trim(), mode: 'colleague' },
      })
    } catch {
      toast.error(t('digital_coworker:new_coworker.colleague_invite.invite_error'))
    } finally {
      setIsSendingInvite(false)
    }
  }

  const isFormValid = name.trim() !== '' && jobTitle.trim() !== '' && voice !== ''
  const getInitials = (n: string) => n.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()

  // Editing an existing coworker reuses this page but is a single step — no
  // wizard progress bar, and edit (not "New") chrome.
  const isEditMode = !!existingCoworkerId

  return (
    <WizardLayout
      step={1}
      totalSteps={type === 'colleague' ? 2 : 4}
      showProgress={!isEditMode}
      breadcrumbLabel={isEditMode ? t('digital_coworker:breadcrumb.edit', 'Edit Digital Coworker') : undefined}
    >
      <h2 className="text-lg font-semibold mb-6">
        {isEditMode
          ? t('digital_coworker:new_coworker.edit_title', 'Edit Digital Coworker')
          : t('digital_coworker:new_coworker.who_for_title', 'Create a Digital Coworker')}
      </h2>

      {/* Type selector cards */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <button
          type="button"
          onClick={() => setType('me')}
          className={`flex flex-col gap-1 rounded-lg border-2 p-4 text-left transition-all ${
            type === 'me'
              ? 'border-[#0e7490] bg-[#0e7490]/5'
              : 'border-[#e5e5e5] dark:border-dark-border hover:border-gray-300'
          }`}
        >
          <span className="text-sm font-medium">{t('digital_coworker:new_coworker.for_me', 'For me')}</span>
          <span className="text-xs text-muted-foreground">
            {t('digital_coworker:new_coworker.for_me_desc', 'Set it up now and make it your own')}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setType('colleague')}
          className={`flex flex-col gap-1 rounded-lg border-2 p-4 text-left transition-all ${
            type === 'colleague'
              ? 'border-[#0e7490] bg-[#0e7490]/5'
              : 'border-[#e5e5e5] dark:border-dark-border hover:border-gray-300'
          }`}
        >
          <span className="text-sm font-medium">{t('digital_coworker:new_coworker.for_colleague', 'For a colleague')}</span>
          <span className="text-xs text-muted-foreground">
            {t('digital_coworker:new_coworker.for_colleague_desc', "They'll get an invite to personalize it themselves")}
          </span>
        </button>
      </div>

      {type === 'me' ? (
        /* For Me form */
        <div className="space-y-5">
          {/* Avatar with picker */}
          <div className="relative flex items-center gap-3">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
            <div
              className="size-10 shrink-0 rounded-full bg-orange-100 flex items-center justify-center font-semibold text-orange-600 text-sm overflow-hidden cursor-pointer"
              onClick={() => setAvatarPickerOpen(true)}
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Avatar" className="size-full object-cover" />
              ) : name ? getInitials(name) : '?'}
            </div>
            <IntuiButton variant="outline" size="sm" onClick={() => setAvatarPickerOpen(true)}>
              {t('digital_coworker:new_coworker.replace_avatar', 'Replace Avatar')}
            </IntuiButton>
            <AvatarPicker
              open={avatarPickerOpen}
              onClose={() => setAvatarPickerOpen(false)}
              onSelectPreset={handleSelectPresetAvatar}
              onUploadFile={(file) => {
                setPhotoFile(file)
                const reader = new FileReader()
                reader.onloadend = () => setPhotoPreview(reader.result as string)
                reader.readAsDataURL(file)
              }}
              selectedUrl={photoPreview}
            />
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.name_label', 'Your name')}<span className="text-destructive">*</span>
            </label>
            <IntuiInput placeholder="e.g. Alex Rivera" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          </div>

          {/* Job title */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.job_title_label', 'Job title')}<span className="text-destructive">*</span>
            </label>
            <IntuiInput placeholder="e.g. Plant Safety Engineer" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="h-9" />
          </div>

          {/* Department */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.department_label', 'Department')}<span className="text-destructive">*</span>
            </label>
            <IntuiSelect value={department} onValueChange={setDepartment}>
              <IntuiSelectTrigger className="h-9">
                <IntuiSelectValue />
              </IntuiSelectTrigger>
              <IntuiSelectContent>
                {DEPARTMENTS.map((d) => (
                  <IntuiSelectItem key={d} value={d}>{d}</IntuiSelectItem>
                ))}
              </IntuiSelectContent>
            </IntuiSelect>
          </div>

          {/* Voice */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.voice_label', 'Voice')}<span className="text-destructive">*</span>
            </label>
            <IntuiRadioGroup
              aria-label={t('digital_coworker:new_coworker.voice_label', 'Voice')}
              orientation="horizontal"
              value={voice}
              onValueChange={(v) => setVoice(v as DigitalCoworkerVoice)}
            >
              {VOICES.map((v) => (
                <IntuiRadioGroupItem
                  key={v}
                  value={v}
                  label={t(`digital_coworker:new_coworker.voices.${v}`, v === 'female' ? 'Female' : 'Male')}
                />
              ))}
            </IntuiRadioGroup>
          </div>

          {/* Expertise — chip/tag input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.expertise_label', 'Areas of Expertise')}
            </label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-input bg-white px-2.5 py-1.5 focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/50 dark:bg-dark-card">
              {expertiseTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => setExpertiseTags((prev) => prev.filter((t) => t !== tag))}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] bg-transparent text-sm outline-none placeholder:text-muted-foreground py-0.5"
                placeholder={expertiseTags.length === 0 ? 'e.g. HAZOP, Process Safety, Compliance' : ''}
                value={expertiseInput}
                onChange={(e) => setExpertiseInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && expertiseInput.trim()) {
                    e.preventDefault()
                    const tag = expertiseInput.trim().replace(/,$/, '')
                    if (tag && !expertiseTags.includes(tag)) {
                      setExpertiseTags((prev) => [...prev, tag])
                    }
                    setExpertiseInput('')
                  }
                  if (e.key === 'Backspace' && !expertiseInput && expertiseTags.length > 0) {
                    setExpertiseTags((prev) => prev.slice(0, -1))
                  }
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4">
            <IntuiButton variant="outline" onClick={() => navigate('/digital-coworker')}>
              {t('common:buttons.cancel', 'Cancel')}
            </IntuiButton>
            <IntuiButton
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              loading={isSubmitting}
              rightIcon={isEditMode ? undefined : <ArrowRight />}
            >
              {isEditMode
                ? t('common:buttons.save', 'Save')
                : t('digital_coworker:new_coworker.continue', 'Continue')}
            </IntuiButton>
          </div>
        </div>
      ) : (
        /* For Colleague form — full form matching Figma */
        <div className="space-y-5">
          {/* Avatar with picker */}
          <div className="relative flex items-center gap-3">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
            <div
              className="size-10 shrink-0 rounded-full bg-orange-100 flex items-center justify-center font-semibold text-orange-600 text-sm overflow-hidden cursor-pointer"
              onClick={() => setAvatarPickerOpen(true)}
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Avatar" className="size-full object-cover" />
              ) : name ? getInitials(name) : '?'}
            </div>
            <IntuiButton variant="outline" size="sm" onClick={() => setAvatarPickerOpen(true)}>
              {t('digital_coworker:new_coworker.replace_avatar', 'Replace Avatar')}
            </IntuiButton>
            <AvatarPicker
              open={avatarPickerOpen}
              onClose={() => setAvatarPickerOpen(false)}
              onSelectPreset={handleSelectPresetAvatar}
              onUploadFile={(file) => {
                setPhotoFile(file)
                const reader = new FileReader()
                reader.onloadend = () => setPhotoPreview(reader.result as string)
                reader.readAsDataURL(file)
              }}
              selectedUrl={photoPreview}
            />
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.colleague_name_label', 'Their name')}<span className="text-destructive">*</span>
            </label>
            <IntuiInput placeholder="e.g. Alex Rivera" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.colleague_invite.email_label', 'Their email address')}<span className="text-destructive">*</span>
            </label>
            <IntuiInput
              type="email"
              placeholder="e.g. alex@example.com"
              value={colleagueEmail}
              onChange={(e) => setColleagueEmail(e.target.value)}
              className="h-9"
            />
          </div>

          {/* Job title */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.job_title_label', 'Job title')}<span className="text-destructive">*</span>
            </label>
            <IntuiInput placeholder="e.g. Plant Safety Engineer" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="h-9" />
          </div>

          {/* Department */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.department_label', 'Department')}<span className="text-destructive">*</span>
            </label>
            <IntuiSelect value={department} onValueChange={setDepartment}>
              <IntuiSelectTrigger className="h-9">
                <IntuiSelectValue />
              </IntuiSelectTrigger>
              <IntuiSelectContent>
                {DEPARTMENTS.map((d) => (
                  <IntuiSelectItem key={d} value={d}>{d}</IntuiSelectItem>
                ))}
              </IntuiSelectContent>
            </IntuiSelect>
          </div>

          {/* Expertise — chip/tag input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('digital_coworker:new_coworker.expertise_label', 'Areas of Expertise')}
            </label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-input bg-white px-2.5 py-1.5 focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/50 dark:bg-dark-card">
              {colleagueExpertiseTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => setColleagueExpertiseTags((prev) => prev.filter((t) => t !== tag))}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] bg-transparent text-sm outline-none placeholder:text-muted-foreground py-0.5"
                placeholder={colleagueExpertiseTags.length === 0 ? 'e.g. HAZOP, Process Safety, Compliance' : ''}
                value={colleagueExpertiseInput}
                onChange={(e) => setColleagueExpertiseInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && colleagueExpertiseInput.trim()) {
                    e.preventDefault()
                    const tag = colleagueExpertiseInput.trim().replace(/,$/, '')
                    if (tag && !colleagueExpertiseTags.includes(tag)) {
                      setColleagueExpertiseTags((prev) => [...prev, tag])
                    }
                    setColleagueExpertiseInput('')
                  }
                  if (e.key === 'Backspace' && !colleagueExpertiseInput && colleagueExpertiseTags.length > 0) {
                    setColleagueExpertiseTags((prev) => prev.slice(0, -1))
                  }
                }}
              />
            </div>
          </div>

          {/* Info alert */}
          {showColleagueAlert && (
            <div className="flex items-start gap-2 rounded-lg border border-[#e5e5e5] p-3 text-sm text-muted-foreground dark:border-dark-border">
              <CircleAlert className="size-4 shrink-0 mt-0.5" />
              <p className="flex-1">{t('digital_coworker:new_coworker.colleague_invite.info', "How does the invite work? They'll receive an email to connect their Google Drive, upload files, and join interviews")}</p>
              <button type="button" onClick={() => setShowColleagueAlert(false)} className="shrink-0 text-muted-foreground hover:text-foreground">
                <X className="size-4" />
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-4">
            <IntuiButton variant="outline" onClick={() => navigate('/digital-coworker')}>
              {t('common:buttons.cancel', 'Cancel')}
            </IntuiButton>
            <IntuiButton
              onClick={handleSendInvite}
              disabled={!colleagueEmail.trim() || !name.trim() || isSendingInvite}
              loading={isSendingInvite}
            >
              {t('digital_coworker:new_coworker.colleague_invite.send_button', 'Send Invite')}
            </IntuiButton>
          </div>
        </div>
      )}
    </WizardLayout>
  )
}
